import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getRoutesAdmin } from '../../services/adminRouteService';
import { AdminRouteData, AdminRouteQueryParams, AdminPaginationMetadata } from '../../types/adminRoute';

interface AdminRouteState {
  routes: AdminRouteData[];
  loading: boolean;
  error: string | null;
  searchParams: AdminRouteQueryParams | null;
  serialNumbers: string[];
  pagination: AdminPaginationMetadata;
}

const initialState: AdminRouteState = {
  routes: [],
  loading: false,
  error: null,
  searchParams: null,
  serialNumbers: [],
  pagination: {
    currentPage: 0,
    totalPages: 0,
    totalItems: 0,
    pageSize: 50,
    hasNext: false,
    hasPrevious: false,
  },
};

// Async thunk for fetching admin routes
export const fetchAdminRoutes = createAsyncThunk(
  'adminRoutes/fetch',
  async ({ params, adminApiKey }: { 
    params: AdminRouteQueryParams & {
      page?: number;
      size?: number;
      sortBy?: string;
      sortDir?: string;
    }; 
    adminApiKey: string 
  }) => {
    const response = await getRoutesAdmin(params, adminApiKey);
    return response;
  }
);

const adminRouteSlice = createSlice({
  name: 'adminRoutes',
  initialState,
  reducers: {
    setAdminSearchParams: (state, action) => {
      state.searchParams = action.payload;
      // Reset to first page when search params change
      state.pagination.currentPage = 0;
    },
    setAdminCurrentPage: (state, action) => {
      state.pagination.currentPage = action.payload;
    },
    clearAdminError: (state) => {
      state.error = null;
    },
    addSerialNumber: (state, action) => {
      const serialNumber = action.payload.trim();
      if (serialNumber && !state.serialNumbers.includes(serialNumber)) {
        state.serialNumbers.push(serialNumber);
      }
    },
    removeSerialNumber: (state, action) => {
      state.serialNumbers = state.serialNumbers.filter(serial => serial !== action.payload);
    },
    clearAllSerialNumbers: (state) => {
      state.serialNumbers = [];
    },
    setSerialNumbers: (state, action) => {
      state.serialNumbers = action.payload;
    },
    resetAdminRoutes: (state) => {
      state.routes = [];
      state.error = null;
      state.searchParams = null;
      state.serialNumbers = [];
      state.pagination = {
        currentPage: 0,
        totalPages: 0,
        totalItems: 0,
        pageSize: 50,
        hasNext: false,
        hasPrevious: false,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdminRoutes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminRoutes.fulfilled, (state, action) => {
        state.loading = false;
        // Check if response is paginated or legacy format
        if (action.payload && typeof action.payload === 'object' && 'content' in action.payload) {
          // Paginated response
          state.routes = action.payload.content;
          state.pagination = action.payload.pagination;
        } else {
          // Legacy response (array of routes)
          state.routes = action.payload as AdminRouteData[];
          state.pagination = {
            currentPage: 0,
            totalPages: 1,
            totalItems: (action.payload as AdminRouteData[]).length,
            pageSize: 50,
            hasNext: false,
            hasPrevious: false,
          };
        }
        state.error = null;
      })
      .addCase(fetchAdminRoutes.rejected, (state, action) => {
        state.loading = false;
        state.routes = [];
        state.error = action.error.message || 'Failed to fetch admin routes';
      });
  },
});

export const {
  setAdminSearchParams,
  setAdminCurrentPage,
  clearAdminError,
  addSerialNumber,
  removeSerialNumber,
  clearAllSerialNumbers,
  setSerialNumbers,
  resetAdminRoutes,
} = adminRouteSlice.actions;

export default adminRouteSlice.reducer; 